import { useState, useEffect, useCallback, createContext, useContext } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { auth, User } from '@/api/client'

// ─── Auth Context ─────────────────────────────────────────────────────────────

export interface AuthContextValue {
  user: User | null
  isLoading: boolean
  isAuthenticated: boolean
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  error: string | null
}

export const AuthContext = createContext<AuthContextValue | null>(null)

// ─── useAuthProvider (used in App.tsx) ───────────────────────────────────────

export function useAuthProvider(): AuthContextValue {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const queryClient = useQueryClient()

  // Fetch current user on mount
  useEffect(() => {
    auth
      .me()
      .then((u) => setUser(u))
      .catch(() => setUser(null))
      .finally(() => setIsLoading(false))
  }, [])

  // Listen for 401 events from axios interceptor
  useEffect(() => {
    const handle = () => {
      setUser(null)
      queryClient.clear()
    }
    window.addEventListener('insulis:unauthorized', handle)
    return () => window.removeEventListener('insulis:unauthorized', handle)
  }, [queryClient])

  const login = useCallback(async (email: string, password: string) => {
    setError(null)
    setIsLoading(true)
    try {
      const u = await auth.login(email, password)
      setUser(u)
    } catch (err: unknown) {
      const message =
        err instanceof Error
          ? err.message
          : 'Invalid email or password. Please try again.'
      setError(message)
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [])

  const logout = useCallback(async () => {
    try {
      await auth.logout()
    } finally {
      setUser(null)
      queryClient.clear()
    }
  }, [queryClient])

  return {
    user,
    isLoading,
    isAuthenticated: user !== null,
    login,
    logout,
    error,
  }
}

// ─── useAuth (consumer hook) ──────────────────────────────────────────────────

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return ctx
}
